import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Button, Dropdown, Input } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import posthog from "posthog-js";
import { axiosBasic } from "../../../../../api/axios";
import { config } from "../../../../../api/endPoints";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";

import "../Settings.css";
import "./Backup.css";

const ReportingAPI = config.REST_API.Reporting;
const DepartmentAPI = config.REST_API.Department;
const OfficeAPI = config.REST_API.Office;

const Backup = () => {
  const { t } = useTranslation();
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();

  // State management
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("yearly");
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [availableYears, setAvailableYears] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);

  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingOffices, setLoadingOffices] = useState(false);
  const [downloading, setDownloading] = useState({});

  const periodOptions = [
    { key: "weekly", text: t("app.reporting.periodWeekly"), value: "weekly" },
    { key: "monthly", text: t("app.reporting.periodMonthly"), value: "monthly" },
    { key: "quarterly", text: t("app.reporting.periodQuarterly"), value: "quarterly" },
    { key: "yearly", text: t("app.reporting.periodYearly"), value: "yearly" },
    { key: "custom", text: t("app.reporting.periodCustomDateRange"), value: "custom" }
  ];

  // Redirect to billing if subscription is suspended
  useEffect(() => {
    if (isSuspended) {
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, history]);

  // Fetch data on component mount
  useEffect(() => {
    if (!isSuspended) {
      fetchAvailableYears();
      fetchDepartments();
      fetchOffices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuspended]);

  // Show restriction message if subscription is suspended
  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  const fetchAvailableYears = async () => {
    setLoadingYears(true);
    try {
      const response = await axiosBasic.get(ReportingAPI.AvailableYears);
      console.log('AvailableYears API Response:', response.data);

      let years = [];

      if (response.data) {
        if (response.data.success && response.data.data) {
          if (response.data.data.years && Array.isArray(response.data.data.years)) {
            years = response.data.data.years;
          } else if (Array.isArray(response.data.data)) {
            years = response.data.data;
          }
        } else if (response.data.data && Array.isArray(response.data.data)) {
          years = response.data.data;
        } else if (Array.isArray(response.data)) {
          years = response.data;
        } else if (response.data.years && Array.isArray(response.data.years)) {
          years = response.data.years;
        }
      }

      if (years.length > 0) {
        years.sort((a, b) => {
          const yearA = typeof a === 'number' ? a : parseInt(a);
          const yearB = typeof b === 'number' ? b : parseInt(b);
          return yearB - yearA;
        });

        const yearOptions = years.map(year => {
          const yearValue = typeof year === 'number' ? year.toString() : String(year).trim();
          return {
            key: yearValue,
            text: yearValue,
            value: yearValue
          };
        });

        setAvailableYears(yearOptions);
        if (!selectedYear) {
          setSelectedYear(yearOptions[0].value);
        }
      } else {
        const currentYear = new Date().getFullYear();
        const fallbackYears = [];
        for (let year = currentYear; year >= 2020; year--) {
          fallbackYears.push(year);
        }
        const yearOptions = fallbackYears.map(year => ({
          key: year.toString(),
          text: year.toString(),
          value: year.toString()
        }));
        setAvailableYears(yearOptions);
        if (!selectedYear) {
          setSelectedYear(currentYear.toString());
        }
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("backupFetchYears", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          status: error?.response?.status,
        });
      }
      const currentYear = new Date().getFullYear();
      const fallbackYears = [];
      for (let year = currentYear; year >= 2020; year--) {
        fallbackYears.push(year);
      }
      const yearOptions = fallbackYears.map(year => ({
        key: year.toString(),
        text: year.toString(),
        value: year.toString()
      }));
      setAvailableYears(yearOptions);
      if (!selectedYear) {
        setSelectedYear(currentYear.toString());
      }
    } finally {
      setLoadingYears(false);
    }
  };

  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await axiosBasic.get(DepartmentAPI.Get);
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        const departmentOptions = response.data.data.map(dept => ({
          key: dept.id,
          text: dept.name,
          value: dept.id
        }));
        setDepartments(departmentOptions);
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("backupFetchDepartments", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          status: error?.response?.status,
        });
      }
    } finally {
      setLoadingDepartments(false);
    }
  };

  const fetchOffices = async () => {
    setLoadingOffices(true);
    try {
      const response = await axiosBasic.get(OfficeAPI.GetCompanyOffices);
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        const officeOptions = response.data.data
          .filter(office => office.isActive !== false && office.isDeleted !== true)
          .map(office => ({
            key: office.id,
            text: office.name,
            value: office.id
          }));
        setOffices(officeOptions);
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("backupFetchOffices", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          status: error?.response?.status,
        });
      }
    } finally {
      setLoadingOffices(false);
    }
  };

  const handleDownloadReport = async (reportId, reportName) => {
    if (!selectedYear && selectedPeriod !== "custom") {
      alert(t("app.reporting.selectYearFirst"));
      return;
    }

    if (selectedPeriod === "custom" && (!customStartDate || !customEndDate)) {
      alert(t("app.reporting.selectCustomDates"));
      return;
    }

    setDownloading(prev => ({ ...prev, [reportId]: true }));

    try {
      const params = {
        year: selectedPeriod !== "custom" ? parseInt(selectedYear) : null,
        period: selectedPeriod,
        format: "excel"
      };

      if (selectedPeriod === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      if (selectedDepartment) {
        params.departmentId = selectedDepartment;
      }

      if (selectedOffice) {
        params.officeId = selectedOffice;
      }

      const endpoint = ReportingAPI[reportId];
      if (!endpoint) {
        throw new Error(`Endpoint not found for report: ${reportId}`);
      }

      const response = await axiosBasic.get(endpoint, {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = `${reportName}_${selectedYear || 'Custom'}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("backupDownload", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Download failed",
          status: error?.response?.status,
          reportName,
        });
      }
      alert(t("app.reporting.downloadFailed", { name: reportName }));
    } finally {
      setDownloading(prev => ({ ...prev, [reportId]: false }));
    }
  };

  const periodLabelKey = (p) => {
    const map = { weekly: "periodWeekly", monthly: "periodMonthly", quarterly: "periodQuarterly", yearly: "periodYearly", custom: "periodCustomDateRange" };
    return t(`app.reporting.${map[p] || p}`);
  };

  const getReportCards = () => [
    { id: "AnnualReport", icon: "chart line", gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)", priority: 1, periods: ["yearly"] },
    { id: "EmployeeDemographics", icon: "users", gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", priority: 1, periods: ["weekly", "monthly", "quarterly", "yearly"] },
    { id: "EmployeeTurnover", icon: "user times", gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", priority: 1, periods: ["monthly", "quarterly", "yearly"] },
    { id: "SalaryCompensation", icon: "dollar sign", gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)", priority: 1, periods: ["monthly", "quarterly", "yearly"] },
    { id: "LeaveUtilization", icon: "calendar check", gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)", priority: 1, periods: ["weekly", "monthly", "quarterly", "yearly"] },
    { id: "EmployeeDevelopment", icon: "graduation cap", gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)", priority: 1, periods: ["quarterly", "yearly"] },
    { id: "EmployeeExperience", icon: "briefcase", gradient: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)", priority: 1, periods: ["monthly", "quarterly", "yearly"] },
    { id: "DisciplinaryActions", icon: "exclamation triangle", gradient: "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)", priority: 2, periods: ["monthly", "quarterly", "yearly"] },
    { id: "DepartmentDistribution", icon: "sitemap", gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)", priority: 2, periods: ["monthly", "quarterly", "yearly"] },
    { id: "EmploymentStatus", icon: "clipboard list", gradient: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)", priority: 2, periods: ["weekly", "monthly", "quarterly", "yearly"] },
    { id: "CustomDateRange", icon: "calendar alternate", gradient: "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)", priority: 2, periods: ["custom"] }
  ];

  // Reports to hide for V2 (coming soon)
  const hiddenReportsV2 = [
    "EmployeeDemographics",
    "EmployeeTurnover",
    "SalaryCompensation",
    "EmployeeDevelopment",
    "EmployeeExperience",
    "DisciplinaryActions",
    "EmploymentStatus",
    "CustomDateRange"
  ];

  const priority1Reports = getReportCards().filter(r =>
    r.priority === 1 && !hiddenReportsV2.includes(r.id)
  );
  const priority2Reports = getReportCards().filter(r =>
    r.priority === 2 && !hiddenReportsV2.includes(r.id)
  );

  const isReportPeriodValid = (report) => {
    if (report.periods.includes(selectedPeriod)) {
      return true;
    }
    return false;
  };

  return (
    <div className="backup-container reporting-container">
      {/* Header Section */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon">
            <Icon name="chart bar" className="m-0 mb-1" />
          </div>
          <div className="section-content">
            <h3 className="section-title">{t("app.reporting.title")}</h3>
            <p className="section-description">
              {t("app.reporting.description")}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="settings-page-content">
        <div className="content-card">
          {/* Info Section */}
          <div className="info-section reporting-info">
            <div className="info-icon">
              <Icon name="info circle" />
            </div>
            <div className="info-text">
              <h3>{t("app.reporting.advancedReporting")}</h3>
              <p>{t("app.reporting.infoDescription")}</p>
            </div>
          </div>

          {/* Filters Section */}
          <div className="reporting-filters-section">
            <h3 className="section-title">
              <Icon name="filter" />
              {t("app.reporting.reportFilters")}
            </h3>
            <div className="filters-grid">
              <div className="filter-group">
                <label className="filter-label">{t("app.reporting.filterYear")}</label>
                <Dropdown
                  placeholder={t("app.reporting.selectYear")}
                  fluid
                  selection
                  loading={loadingYears}
                  options={availableYears}
                  value={selectedYear}
                  onChange={(e, { value }) => setSelectedYear(value)}
                  className="reporting-filter-dropdown"
                  disabled={loadingYears || availableYears.length === 0 || selectedPeriod === "custom"}
                />
              </div>

              <div className="filter-group">
                <label className="filter-label">{t("app.reporting.filterPeriod")}</label>
                <Dropdown
                  placeholder={t("app.reporting.selectPeriod")}
                  fluid
                  selection
                  options={periodOptions}
                  value={selectedPeriod}
                  onChange={(e, { value }) => {
                    setSelectedPeriod(value);
                    if (value !== "custom" && !selectedYear && availableYears.length > 0) {
                      setSelectedYear(availableYears[0].value);
                    }
                  }}
                  className="reporting-filter-dropdown"
                />
              </div>

              {selectedPeriod === "custom" && (
                <>
                  <div className="filter-group">
                    <label className="filter-label">{t("app.reporting.startDate")}</label>
                    <Input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="reporting-date-input"
                      fluid
                    />
                  </div>
                  <div className="filter-group">
                    <label className="filter-label">{t("app.reporting.endDate")}</label>
                    <Input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="reporting-date-input"
                      fluid
                    />
                  </div>
                </>
              )}

              <div className="filter-group">
                <label className="filter-label">{t("app.reporting.departmentOptional")}</label>
                <Dropdown
                  placeholder={t("app.reporting.allDepartments")}
                  fluid
                  selection
                  clearable
                  loading={loadingDepartments}
                  options={departments}
                  value={selectedDepartment}
                  onChange={(e, { value }) => setSelectedDepartment(value)}
                  className="reporting-filter-dropdown"
                  disabled={loadingDepartments}
                />
              </div>

              <div className="filter-group">
                <label className="filter-label">{t("app.reporting.officeOptional")}</label>
                <Dropdown
                  placeholder={t("app.reporting.allOffices")}
                  fluid
                  selection
                  clearable
                  loading={loadingOffices}
                  options={offices}
                  value={selectedOffice}
                  onChange={(e, { value }) => setSelectedOffice(value)}
                  className="reporting-filter-dropdown"
                  disabled={loadingOffices}
                />
              </div>
            </div>
          </div>

          {/* Priority 1 Reports */}
          <div className="reports-section">
            <h3 className="section-title">
              <Icon name="star" />
              {t("app.reporting.highValueReports")}
            </h3>
            <p className="section-description">{t("app.reporting.highValueReportsDescription")}</p>
            <div className="reports-grid">
              {priority1Reports.map(report => {
                const reportTitle = t(`app.reporting.reports.${report.id}.title`);
                const reportDescription = t(`app.reporting.reports.${report.id}.description`);
                const reportFeatures = t(`app.reporting.reports.${report.id}.features`, { returnObjects: true });
                const featuresList = Array.isArray(reportFeatures) ? reportFeatures : [];
                const isValidPeriod = isReportPeriodValid(report);
                const isDownloading = downloading[report.id] || false;
                const canDownload = isValidPeriod && (selectedPeriod !== "custom" ? selectedYear : (customStartDate && customEndDate));

                return (
                  <div key={report.id} className={`report-card ${!isValidPeriod ? 'disabled' : ''}`}>
                    <div className="report-header">
                      <div className="report-icon" style={{ background: report.gradient }}>
                        <Icon name={report.icon} />
                      </div>
                      <div className="report-info">
                        <h4 className="report-title">{reportTitle}</h4>
                        <p className="report-description">{reportDescription}</p>
                      </div>
                    </div>

                    <div className="report-features">
                      <h5>{t("app.reporting.includes")}</h5>
                      <ul className="features-list">
                        {featuresList.map((feature, index) => (
                          <li key={index} className="feature-item">
                            <Icon name="check circle" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="report-meta">
                      <div className="report-periods">
                        <Icon name="calendar" />
                        <span>{t("app.reporting.available")} {report.periods.map(periodLabelKey).join(", ")}</span>
                      </div>
                    </div>

                    <div className="report-actions">
                      <button
                        className="download-button"
                        onClick={() => handleDownloadReport(report.id, reportTitle)}
                        disabled={!canDownload || isDownloading}
                        style={{
                          background: !canDownload || isDownloading ? "#cbd5e0" : report.gradient,
                          cursor: !canDownload || isDownloading ? "not-allowed" : "pointer",
                          opacity: !canDownload ? 0.6 : 1
                        }}
                      >
                        <Icon name={isDownloading ? "spinner" : "download"} loading={isDownloading} />
                        {isDownloading ? t("app.reporting.downloading") : t("app.reporting.downloadReport", { name: reportTitle })}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Priority 2 Reports */}
          <div className="reports-section">
            <h3 className="section-title">
              <Icon name="file alternate" />
              {t("app.reporting.specializedReports")}
            </h3>
            <p className="section-description">{t("app.reporting.specializedReportsDescription")}</p>
            <div className="reports-grid">
              {priority2Reports.map(report => {
                const reportTitle = t(`app.reporting.reports.${report.id}.title`);
                const reportDescription = t(`app.reporting.reports.${report.id}.description`);
                const reportFeatures = t(`app.reporting.reports.${report.id}.features`, { returnObjects: true });
                const featuresList = Array.isArray(reportFeatures) ? reportFeatures : [];
                const isValidPeriod = isReportPeriodValid(report);
                const isDownloading = downloading[report.id] || false;
                const canDownload = isValidPeriod && (selectedPeriod !== "custom" ? selectedYear : (customStartDate && customEndDate));

                return (
                  <div key={report.id} className={`report-card ${!isValidPeriod ? 'disabled' : ''}`}>
                    <div className="report-header">
                      <div className="report-icon" style={{ background: report.gradient }}>
                        <Icon name={report.icon} />
                      </div>
                      <div className="report-info">
                        <h4 className="report-title">{reportTitle}</h4>
                        <p className="report-description">{reportDescription}</p>
                      </div>
                    </div>

                    <div className="report-features">
                      <h5>{t("app.reporting.includes")}</h5>
                      <ul className="features-list">
                        {featuresList.map((feature, index) => (
                          <li key={index} className="feature-item">
                            <Icon name="check circle" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="report-meta">
                      <div className="report-periods">
                        <Icon name="calendar" />
                        <span>{t("app.reporting.available")} {report.periods.map(periodLabelKey).join(", ")}</span>
                      </div>
                    </div>

                    <div className="report-actions">
                      <button
                        className="download-button"
                        onClick={() => handleDownloadReport(report.id, reportTitle)}
                        disabled={!canDownload || isDownloading}
                        style={{
                          background: !canDownload || isDownloading ? "#cbd5e0" : report.gradient,
                          cursor: !canDownload || isDownloading ? "not-allowed" : "pointer",
                          opacity: !canDownload ? 0.6 : 1
                        }}
                      >
                        <Icon name={isDownloading ? "spinner" : "download"} loading={isDownloading} />
                        {isDownloading ? t("app.reporting.downloading") : t("app.reporting.downloadReport", { name: reportTitle })}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Backup;
